import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  CardHeader,
} from '@mui/material';
import {
  AccountTree,
  Assignment,
  CloudDownload,
  Search,
  ContentCopy,
  Notifications,
  Storage,
  Palette,
  Dashboard,
  Group,
  FileDownload,
  Assessment,
} from '@mui/icons-material';

const README: React.FC = () => {
  const features = [
    {
      title: 'RAPID Decision Framework',
      icon: <AccountTree />,
      description: 'Complete implementation of the RAPID decision-making framework with role-based assignments',
      details: [
        'R (Recommend) - Owns decision process',
        'A (Agree) - Ensures feasibility', 
        'P (Perform) - Executes decisions',
        'I (Input) - Provides input',
        'D (Decide) - Makes final decision'
      ]
    },
    {
      title: 'Project Management',
      icon: <Assignment />,
      description: 'Comprehensive project management with templates and customization',
      details: [
        'Multiple project support',
        '5 pre-built project templates',
        'Project metadata management',
        'Task organization by work areas'
      ]
    },
    {
      title: 'Data Export & Import',
      icon: <CloudDownload />,
      description: 'Export your matrices in multiple professional formats',
      details: [
        'Excel (.xlsx) with multiple sheets',
        'CSV format for data analysis',
        'PDF reports with formatting',
        'JSON for data backup'
      ]
    },
    {
      title: 'Advanced Search & Filtering',
      icon: <Search />,
      description: 'Find tasks and projects quickly with intelligent search',
      details: [
        'Real-time search across tasks',
        'Filter by work areas',
        'Search project descriptions',
        'Quick navigation'
      ]
    },
    {
      title: 'Task Management',
      icon: <ContentCopy />,
      description: 'Efficient task creation and management tools',
      details: [
        'Clone tasks with one click',
        'Dynamic task creation',
        'Bulk operations support',
        'Task validation warnings'
      ]
    },
    {
      title: 'Smart Notifications',
      icon: <Notifications />,
      description: 'Stay informed with contextual feedback',
      details: [
        'Success confirmations',
        'Error handling alerts',
        'Validation warnings',
        'Export status updates'
      ]
    },
    {
      title: 'Data Persistence',
      icon: <Storage />,
      description: 'Your work is automatically saved and restored',
      details: [
        'Local storage integration',
        'Automatic data backup',
        'Session recovery',
        'No data loss protection'
      ]
    },
    {
      title: 'Dark Mode Support',
      icon: <Palette />,
      description: 'Modern dark theme for comfortable viewing',
      details: [
        'Toggle between light and dark themes',
        'Consistent color scheme',
        'Eye-friendly design',
        'Professional appearance'
      ]
    }
  ];

  const rapidRoles = [
    { role: 'R', name: 'Recommend', color: '#d32f2f', description: 'Owns and drives decision process' },
    { role: 'A', name: 'Agree', color: '#1976d2', description: 'Ensures recommendation is feasible' },
    { role: 'P', name: 'Perform', color: '#f57c00', description: 'Executes decisions' },
    { role: 'I', name: 'Input', color: '#757575', description: 'Provides input' },
    { role: 'D', name: 'Decide', color: '#388e3c', description: 'Makes final decision' }
  ];

  const rasciRoles = [
    { role: 'R', name: 'Responsible', color: '#d32f2f', description: 'Does the work to complete the task' },
    { role: 'A', name: 'Accountable', color: '#388e3c', description: 'Ultimately answerable for completion' },
    { role: 'S', name: 'Support', color: '#f57c00', description: 'Provides resources or assistance' },
    { role: 'C', name: 'Consulted', color: '#1976d2', description: 'Two-way communication, provides input' },
    { role: 'I', name: 'Informed', color: '#757575', description: 'Kept updated on progress' }
  ];

  return (
    <Box sx={{ maxWidth: '100%', mx: 'auto', p: 3 }}>
      {/* Header */}
      <Paper sx={{ p: 4, mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
          RAPID & RASCI Decision Matrices
        </Typography>
        <Typography variant="h6" color="text.secondary" paragraph>
          A comprehensive dual-matrix tool for implementing both RAPID (decision-making) and RASCI (task responsibility) frameworks in your organization
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1, mt: 2 }}>
          <Chip icon={<Dashboard />} label="Project Management" color="primary" />
          <Chip icon={<Group />} label="Team Collaboration" color="secondary" />
          <Chip icon={<Assessment />} label="Decision Tracking" />
          <Chip icon={<FileDownload />} label="Data Export" />
        </Box>
      </Paper>

      {/* Framework Overview */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountTree /> RAPID Framework (Decision Making)
        </Typography>
        <Typography variant="body1" paragraph>
          The RAPID framework is a decision-making tool that clarifies who makes decisions and how,
          ensuring efficient and accountable decision processes in organizations.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {rapidRoles.map((role) => (
            <Box key={role.role} sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Card sx={{ height: '100%', border: `2px solid ${role.color}` }}>
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={role.role}
                        sx={{ 
                          backgroundColor: role.color, 
                          color: 'white',
                          fontWeight: 'bold',
                          minWidth: 32
                        }}
                      />
                      {role.name}
                    </Box>
                  }
                  sx={{ pb: 1 }}
                />
                <CardContent sx={{ pt: 0 }}>
                  <Typography variant="body2" color="text.secondary">
                    {role.description}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* RASCI Framework Overview */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountTree /> RASCI Framework (Task Responsibility)
        </Typography>
        <Typography variant="body1" paragraph>
          The RASCI framework is a responsibility assignment matrix that clarifies who does what work,
          ensuring clear accountability and task ownership in project execution.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {rasciRoles.map((role) => (
            <Box key={role.role} sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Card sx={{ height: '100%', border: `2px solid ${role.color}` }}>
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={role.role}
                        sx={{
                          backgroundColor: role.color,
                          color: 'white',
                          fontWeight: 'bold',
                          minWidth: 32
                        }}
                      />
                      {role.name}
                    </Box>
                  }
                  sx={{ pb: 1 }}
                />
                <CardContent sx={{ pt: 0 }}>
                  <Typography variant="body2" color="text.secondary">
                    {role.description}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Features Grid */}
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Key Features & Capabilities
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3 }}>
        {features.map((feature, index) => (
          <Box key={index}>
            <Card sx={{ height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
              <CardHeader
                avatar={
                  <Box sx={{ 
                    p: 1, 
                    borderRadius: '50%', 
                    backgroundColor: 'primary.main', 
                    color: 'white',
                    display: 'flex'
                  }}>
                    {feature.icon}
                  </Box>
                }
                title={feature.title}
                titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
              />
              <CardContent>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {feature.description}
                </Typography>
                <List dense>
                  {feature.details.map((detail, idx) => (
                    <ListItem key={idx} sx={{ py: 0.5, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 20 }}>
                        <Box sx={{ 
                          width: 6, 
                          height: 6, 
                          borderRadius: '50%', 
                          backgroundColor: 'primary.main' 
                        }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={detail} 
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {/* Technical Stack */}
      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Technical Stack
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom>Frontend</Typography>
            <List>
              <ListItem><ListItemText primary="React 18 with TypeScript" /></ListItem>
              <ListItem><ListItemText primary="Material-UI (MUI) v7" /></ListItem>
              <ListItem><ListItemText primary="React Hooks & Context" /></ListItem>
              <ListItem><ListItemText primary="Local Storage API" /></ListItem>
            </List>
          </Box>
          <Box>
            <Typography variant="h6" gutterBottom>Libraries & Tools</Typography>
            <List>
              <ListItem><ListItemText primary="SheetJS for Excel export" /></ListItem>
              <ListItem><ListItemText primary="jsPDF for PDF generation" /></ListItem>
              <ListItem><ListItemText primary="React Testing Library" /></ListItem>
              <ListItem><ListItemText primary="Jest for testing" /></ListItem>
            </List>
          </Box>
        </Box>
      </Paper>

      {/* Usage Guide */}
      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Getting Started
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
          <Box>
            <Typography variant="h6" gutterBottom>1. Create or Select Project</Typography>
            <Typography variant="body2" paragraph>
              Start by creating a new project or selecting from existing ones. 
              Use project templates for quick setup.
            </Typography>
            
            <Typography variant="h6" gutterBottom>2. Add Tasks & Work Areas</Typography>
            <Typography variant="body2" paragraph>
              Define your tasks and organize them by work areas. 
              Each task should have a clear description.
            </Typography>
          </Box>
          <Box>
            <Typography variant="h6" gutterBottom>3. Assign RAPID Roles</Typography>
            <Typography variant="body2" paragraph>
              Click on matrix cells to assign RAPID roles to team members. 
              Each person can have multiple roles per task.
            </Typography>
            
            <Typography variant="h6" gutterBottom>4. Export & Share</Typography>
            <Typography variant="body2" paragraph>
              Export your completed matrix in Excel, PDF, or other formats 
              to share with stakeholders.
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Footer */}
      <Box sx={{ textAlign: 'center', mt: 4, p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Created by Fernando Izeta - August 2025
        </Typography>
      </Box>
    </Box>
  );
};

export default README;